import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STAGES, stagePillClass, initials } from "@/lib/cc";
import { useCandidateDrawer } from "@/components/CandidateDrawer";
import { Search } from "lucide-react";

export default function Pipeline() {
  const [data, setData] = useState({});
  const [query, setQuery] = useState("");
  const { open } = useCandidateDrawer();

  const load = async () => {
    const { data } = await api.get("/dashboard/pipeline");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const visible = STAGES.filter((s) => s.key !== "rejected");

  const match = (c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return `${c.first_name} ${c.last_name} ${c.job_title}`.toLowerCase().includes(q);
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Recruitment</div>
          <h1 className="font-display text-4xl font-bold">Pipeline</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Drag-and-drop candidate flow across recruitment stages.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cc-text-soft)]" />
          <input className="cc-input pl-9 w-72" placeholder="Search candidates…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pipeline-search-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-4">
        {visible.map((s) => {
          const items = (data[s.key] || []).filter(match);
          return (
            <div key={s.key} className="kanban-col p-3" data-testid={`pipeline-column-${s.key}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="font-dash font-bold text-sm">{s.label}</span>
                  <span className={`pill ${stagePillClass(s.key)} !py-0.5 !px-2 text-[10px]`}>{items.length}</span>
                </div>
              </div>
              <div className="cc-scroll overflow-y-auto pr-1 stagger" style={{ maxHeight: "calc(100vh - 220px)" }}>
                {items.map((c) => {
                  const refs = c.references || [];
                  const received = refs.filter((r) => r.status === "received" || r.status === "verified").length;
                  const awaiting = refs.filter((r) => r.status === "awaiting").length;
                  const flagged = refs.filter((r) => r.status === "flagged").length;
                  const refLabel = refs.length > 0
                    ? `${received}/${refs.length}${awaiting ? ` · ${awaiting} awaiting` : ""}${flagged ? ` · ${flagged} flagged` : ""}`
                    : null;
                  const refClass = flagged > 0
                    ? "pill-danger"
                    : (refs.length > 0 && received === refs.length ? "pill-success" : "pill-warning");
                  return (
                    <div key={c.id} className="k-card" onClick={() => open(c.id)} data-testid={`pipeline-card-${c.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                             style={{ background: "var(--cc-primary)" }}>
                          {initials(c.first_name, c.last_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{c.first_name} {c.last_name}</div>
                          <div className="text-xs text-[var(--cc-text-soft)] truncate">{c.job_title}</div>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.dbs?.status === "clear" && <span className="pill pill-success !text-[10px] !py-0">DBS</span>}
                        {c.rtw?.status === "verified" && <span className="pill pill-success !text-[10px] !py-0">RTW</span>}
                        {c.rtw?.nationality_status === "immigrant" && <span className="pill pill-info !text-[10px] !py-0">Visa</span>}
                        {refLabel && (
                          <span className={`pill ${refClass} !text-[10px] !py-0`}
                                data-testid={`pipeline-card-refs-${c.id}`}
                                title={`References: ${received} received, ${awaiting} awaiting, ${flagged} flagged`}>
                            Refs {refLabel}
                          </span>
                        )}
                        {c.offer && <span className="pill pill-warning !text-[10px] !py-0">{c.offer.type}</span>}
                      </div>
                    </div>
                  );
                })}
                {!items.length && <div className="text-xs text-[var(--cc-text-soft)] py-6 text-center">No candidates</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
